import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Imports FR
import frCommon from './locales/fr/common.json';
import frAuth from './locales/fr/auth.json';
import frDashboard from './locales/fr/dashboard.json';
import frQuestionnaire from './locales/fr/questionnaire.json';
import frDocuments from './locales/fr/documents.json';
import frPackages from './locales/fr/packages.json';
import frCompletion from './locales/fr/completion.json';
import frLegal from './locales/fr/legal.json';
import frEmails from './locales/fr/emails.json';
import frAi from './locales/fr/ai.json';
import frSatisfaction from './locales/fr/satisfaction.json';
import frSummary from './locales/fr/summary.json';
import frAbout from './locales/fr/about.json';
import frProfile from './locales/fr/profile.json';
import frPreliminary from './locales/fr/preliminary.json';
import frFollowup from './locales/fr/followup.json';
import frAppointments from './locales/fr/appointments.json';
import frAdmin from './locales/fr/admin.json';
import frConsultant from './locales/fr/consultant.json';
import frCareer from './locales/fr/career.json';

// Imports EN
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enQuestionnaire from './locales/en/questionnaire.json';
import enDocuments from './locales/en/documents.json';
import enPackages from './locales/en/packages.json';
import enCompletion from './locales/en/completion.json';
import enLegal from './locales/en/legal.json';
import enEmails from './locales/en/emails.json';
import enAi from './locales/en/ai.json';
import enSatisfaction from './locales/en/satisfaction.json';
import enSummary from './locales/en/summary.json';
import enAbout from './locales/en/about.json';
import enProfile from './locales/en/profile.json';
import enPreliminary from './locales/en/preliminary.json';
import enFollowup from './locales/en/followup.json';
import enAppointments from './locales/en/appointments.json';
import enAdmin from './locales/en/admin.json';
import enConsultant from './locales/en/consultant.json';
import enCareer from './locales/en/career.json';

// Imports ES
import esCommon from './locales/es/common.json';
import esAuth from './locales/es/auth.json';
import esDashboard from './locales/es/dashboard.json';
import esQuestionnaire from './locales/es/questionnaire.json';
import esDocuments from './locales/es/documents.json';
import esPackages from './locales/es/packages.json';
import esCompletion from './locales/es/completion.json';
import esLegal from './locales/es/legal.json';
import esEmails from './locales/es/emails.json';
import esAi from './locales/es/ai.json';
import esSatisfaction from './locales/es/satisfaction.json';
import esSummary from './locales/es/summary.json';
import esAbout from './locales/es/about.json';
import esProfile from './locales/es/profile.json';
import esPreliminary from './locales/es/preliminary.json';
import esFollowup from './locales/es/followup.json';
import esAppointments from './locales/es/appointments.json';
import esAdmin from './locales/es/admin.json';
import esConsultant from './locales/es/consultant.json';
import esCareer from './locales/es/career.json';

export const supportedLanguages = {
  fr: { name: 'Français', flag: '🇫🇷' },
  en: { name: 'English', flag: '🇬🇧' },
  es: { name: 'Español', flag: '🇪🇸' },
} as const;

export type SupportedLanguage = keyof typeof supportedLanguages;

const resources = {
  fr: {
    common: frCommon,
    auth: frAuth,
    dashboard: frDashboard,
    questionnaire: frQuestionnaire,
    documents: frDocuments,
    packages: frPackages,
    completion: frCompletion,
    legal: frLegal,
    emails: frEmails,
    ai: frAi,
    satisfaction: frSatisfaction,
    summary: frSummary,
    about: frAbout,
    profile: frProfile,
    preliminary: frPreliminary,
    followup: frFollowup,
    appointments: frAppointments,
    admin: frAdmin,
    consultant: frConsultant,
    career: frCareer,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    questionnaire: enQuestionnaire,
    documents: enDocuments,
    packages: enPackages,
    completion: enCompletion,
    legal: enLegal,
    emails: enEmails,
    ai: enAi,
    satisfaction: enSatisfaction,
    summary: enSummary,
    about: enAbout,
    profile: enProfile,
    preliminary: enPreliminary,
    followup: enFollowup,
    appointments: enAppointments,
    admin: enAdmin,
    consultant: enConsultant,
    career: enCareer,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    dashboard: esDashboard,
    questionnaire: esQuestionnaire,
    documents: esDocuments,
    packages: esPackages,
    completion: esCompletion,
    legal: esLegal,
    emails: esEmails,
    ai: esAi,
    satisfaction: esSatisfaction,
    summary: esSummary,
    about: esAbout,
    profile: esProfile,
    preliminary: esPreliminary,
    followup: esFollowup,
    appointments: esAppointments,
    admin: esAdmin,
    consultant: esConsultant,
    career: esCareer,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'fr',
    defaultNS: 'common',
    ns: [
      'common', 'auth', 'dashboard', 'questionnaire', 'documents', 'packages',
      'completion', 'legal', 'emails', 'ai', 'satisfaction', 'summary', 'about',
      'profile', 'preliminary', 'followup', 'appointments', 'admin', 'consultant', 'career'
    ],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'bilan-easy-language',
      caches: ['localStorage'],
    },
  });

export default i18n;
